import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Link } from "@inertiajs/react"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { projectShowPath } from "@/lib/project-configs"
import type { Project } from "@/types"

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  house: "Maison",
  apartment: "Appartement",
  land: "Terrain",
  building: "Immeuble",
  commercial: "Commercial",
  mixed: "Mixte",
  other: "Autre",
}

type Props = {
  project: Project
}

export function KanbanCard({ project }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: project.id,
    data: { project },
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <Card ref={setNodeRef} style={style} className="cursor-grab active:cursor-grabbing" {...attributes} {...listeners}>
      <CardContent className="p-3 space-y-2">
        <Link
          href={projectShowPath(project)}
          className="font-medium text-sm hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {project.name}
        </Link>
        <div className="flex items-center gap-1 flex-wrap">
          {project.city_name && <span className="text-muted-foreground text-xs">{project.city_name}</span>}
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            {PROPERTY_TYPE_LABELS[project.property_type] || project.property_type}
          </Badge>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-xs">Score : {project.lead_score}/10</span>
          {project.assigned_users && project.assigned_users.length > 0 && (
            <div className="flex -space-x-1">
              {project.assigned_users.slice(0, 3).map((u) => (
                <div
                  key={u.id}
                  className="bg-muted flex size-5 items-center justify-center rounded-full border text-[10px] font-medium"
                  title={`${u.first_name} ${u.last_name}`}
                >
                  {u.first_name[0]}
                  {u.last_name[0]}
                </div>
              ))}
            </div>
          )}
        </div>
        {project.project_tags && project.project_tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {project.project_tags.map((tag) => (
              <span
                key={tag.id}
                className="rounded-full border px-1.5 py-0 text-[10px] font-medium leading-4"
                style={{ borderColor: tag.color, color: tag.color }}
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
